import { memo, useLayoutEffect, useRef } from 'react';
import { useTheme } from '@/modules/common';
import { bridgeAtom, useValue } from '@/store';
import { Input as StyledInput, TextArea } from './Input.styled';

export interface InputProps {
  value: string;
  placeholder: string;
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  onSend: () => void;
  focus?: boolean;
  disabled?: boolean;
  maxLength?: number;
  hasAttachmentsButton?: boolean;
}

export const Input = memo<InputProps>((props) => {
  const { value, placeholder, onChange, onSend, disabled, focus, maxLength, hasAttachmentsButton } = props;

  const { autofocusInput } = useValue(bridgeAtom);
  const { inputType } = useTheme();

  const inputTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useLayoutEffect(() => {
    const textarea = inputTextareaRef.current;
    if (!textarea || inputType !== 'textarea') {
      return;
    }

    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
  }, [value, inputType]);

  useLayoutEffect(() => {
    const input = inputTextareaRef?.current || inputRef.current;
    if (focus && !disabled && autofocusInput) {
      input?.focus();
    }
  }, [focus, disabled, autofocusInput]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing && event.keyCode !== 229) {
      event.preventDefault();
      onSend();
    }
  };

  if (inputType === 'text') {
    return (
      <StyledInput
        type="text"
        value={value}
        data-testid="saywhatt-chat-input-wrapper"
        name="saywhatt-message-input"
        ref={inputRef}
        disabled={disabled}
        onChange={onChange}
        maxLength={maxLength}
        placeholder={placeholder}
        onKeyDown={handleKeyDown}
        hasAttachmentsButton={hasAttachmentsButton}
      />
    );
  }

  return (
    <TextArea
      value={value}
      data-testid="saywhatt-chat-input-wrapper"
      name="saywhatt-message-input"
      ref={inputTextareaRef}
      disabled={disabled}
      onChange={onChange}
      maxLength={maxLength}
      placeholder={placeholder}
      onKeyDown={handleKeyDown}
      rows={1}
      hasAttachmentsButton={hasAttachmentsButton}
    />
  );
});

Input.displayName = 'Input';
